/*
 * Event short URLs — `<event_link>.apsession.com`.
 *
 * This is the PRIMARY path, not a fallback. The short host is served in place
 * with a 200 (routes/web.php returns the same `welcome` view as everywhere
 * else), so the browser is what turns the hostname into an event: DynamicHome
 * reads the label below and renders <EventDetails/> for it.
 *
 * It used to be a fallback, back when the server answered the short host with
 * a 301 to /events/<slug>. That redirect is gone — it bounced every visitor
 * off the address they actually typed. Ranking is consolidated by a
 * server-rendered <link rel="canonical"> instead; see App\Support\EventSubdomain.
 *
 * WHY NOT COUNT LABELS. This replaces `hostname.split('.').length > 3`, which
 * assumed the site was served from `www.apsession.com` so an event host would
 * carry a 4th label. The canonical origin is the bare apex, so an event host
 * has exactly 3 labels and that test was never true — every short URL rendered
 * the home page. The rule is the one the server applies: at least three
 * labels, and a first label that is not reserved infrastructure.
 */

/** Mirrors App\Support\EventSubdomain::RESERVED — keep the two in step. */
const RESERVED = new Set([
    'www', 'api', 'manager', 'admin', 'app', 'apps', 'mail', 'email',
    'smtp', 'imap', 'ftp', 'cdn', 'assets', 'static', 'media', 'img',
    'staging', 'stage', 'dev', 'test', 'qa', 'demo', 'beta', 'blog',
    'support', 'help', 'status', 'docs', 'my', 'm', 'go', 'link',
]);

/**
 * Mirrors App\Support\EventSubdomain's label pattern — keep the two in step.
 *
 * Underscores are allowed as well as hyphens. The identifier a short host
 * carries is the event's `event_link`, legacy data written in `grand_opening`
 * form where a generated slug would read `grand-opening`; the hyphen-only
 * shape rejected every underscored short host and fell through to the home
 * page.
 */
const SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

/**
 * The event identifier this hostname is asking for, or null when the host is
 * the apex, www, reserved infrastructure, an IP, or localhost.
 */
export function getEventSubdomainSlug(hostname) {
    const host = String(
        hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
    )
        .trim()
        .toLowerCase()
        .replace(/\.$/, '');

    if (!host) return null;

    const parts = host.split('.');

    // Apex (`apsession.com`) and single-name hosts (`localhost`) have nothing
    // in front of the domain to read a slug from.
    if (parts.length < 3) return null;

    // An IPv4 literal splits into 4 numeric labels and would otherwise look
    // like a subdomain of `2.3.4`.
    if (parts.every((part) => /^\d+$/.test(part))) return null;

    const label = parts[0];

    if (RESERVED.has(label) || !SLUG.test(label)) return null;

    return label;
}

export default getEventSubdomainSlug;
